import { PhoneNumberValue } from '@/components/form/PhoneNumberField';
import { handleBEFormError } from '@/helpers/beFormErrorHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useAvatarEdit } from '@/services/avatar/useAvatarEdit';
import { FC, Suspense, useEffect, useRef } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmissionErrorBoundary } from '../../../../components/errors/SubmissionErrorBoundary';
import { CurrencyField } from '../../../../components/form/CurrencyField';
import { TsForm } from '../../../../components/form/Form';
import { TSTitleCard } from '../../../../components/shared/card/Card';
import { TableLoader } from '../../../../components/shared/table/TableLoader';
import { denormalizePhoneNumber, inferPhoneRegion, normalizePhoneNumber } from '../../../../helpers/phoneHelper';
import { useAuthenticationService } from '../../../../services/authentication/authentication-service';
import { Feature } from '../../../../services/backstage/Feature';
import { useEmployeeAvatar } from '../../../../services/employee/EmployeeAvatarProvider';
import { useEmployeeAvatarApi } from '../../../../services/employee/useEmployeeAvatarApi';
import { ReadEmployee, UpdateEmployee, useUpdateStaffMutation } from '../../../../services/gql/graphql.generated';
import { useLocationService } from '../../../../services/location/LocationService';
import strings from '../../team.translations';
import { TeamMemberAddOrEditFormContent } from '../../TeamMemberAddOrEditFormContent';
import { TSTitleCardStyle } from '../AddSingleTeamMemberForm';
import { EmployeeAvatarEditCard } from './EmployeeAvatarEditCard';
import { FormActionButtons } from './FormActionButtons';
import { readEmployeeToUpdateEmployee } from '@/helpers/employeeHelper';

interface TeamMemberEditFormProps {
  employee: ReadEmployee;
  currentLocationId?: number;
  refetchEmployee?: (opts?: any) => void;
}

export interface EditTeamMemberFields {
  firstName: string;
  lastName: string;
  role: string;
  email?: string;
  phoneNumber?: PhoneNumberValue;
  payRate: number;
  locationIds: string[];
}

const toPayload = (originalEmployee: ReadEmployee, data: EditTeamMemberFields, checkedLocationIds: number[]): UpdateEmployee => ({
  ...readEmployeeToUpdateEmployee(originalEmployee),
  firstName: data.firstName,
  lastName: data.lastName,
  role: data.role,
  locationIds: checkedLocationIds,
  email: data.email || undefined,
  phoneNumber: data.phoneNumber?.number
    ? normalizePhoneNumber(data.phoneNumber.number, data.phoneNumber.region)
    : undefined
});

export const TeamMemberDetailsEditForm: FC<TeamMemberEditFormProps> = ({
  employee,
  currentLocationId,
  refetchEmployee
}) => {
  const { scrollPayIntoView } = useParams<'scrollPayIntoView'>();
  const paySectionRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const mappedLocations = useLocationService(state => state.mappedLocations);
  const [user] = useAuthenticationService(state => [state.user]);
  const { notifyEmployeeAvatarUpdated } = useEmployeeAvatar();
  const { uploadAvatar, removeAvatar } = useEmployeeAvatarApi(employee.identityId);
  const [{ fetching }, updateEmployee] = useUpdateStaffMutation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();

  const { avatarUrl, uploadingAvatar, removingAvatar, handleAvatarSelected, handleAvatarRemoved } = useAvatarEdit({
    avatarUrl: employee.avatarUrl,
    uploadAvatar,
    removeAvatar,
    notifyAvatarUpdated: () => notifyEmployeeAvatarUpdated(employee.identityId),
    fileInputRef,
    canUploadOrRemove: () => !!user?.access_token,
    onUploadSuccess: () => refetchEmployee?.({ requestPolicy: 'network-only' }),
    onRemoveSuccess: () => refetchEmployee?.({ requestPolicy: 'network-only' }),
    messages: {
      avatarInvalidFile: strings.editTeamMember.avatarInvalidFile,
      avatarTooLarge: strings.editTeamMember.avatarTooLarge,
      avatarUploadSuccess: strings.editTeamMember.avatarUploadSuccess,
      avatarUploadError: strings.editTeamMember.avatarUploadError,
      avatarRemoveSuccess: strings.editTeamMember.avatarRemoveSuccess,
      avatarRemoveError: strings.editTeamMember.avatarRemoveError
    }
  });

  const initialRegion = inferPhoneRegion(employee.phoneNumber);

  const methods = useForm<EditTeamMemberFields>({
    defaultValues: {
      firstName: employee.firstName,
      lastName: employee.lastName,
      role: employee.role,
      email: employee.email,
      phoneNumber: {
        number: denormalizePhoneNumber(employee.phoneNumber, initialRegion) ?? '',
        region: initialRegion
      },
      payRate: 0,
      locationIds: employee.locations?.map(loc => loc.id.toString())
    }
  });

  const goBack = () => navigate(-1);

  const onSubmit = async (data: EditTeamMemberFields) => {
    if (data.payRate < 0) {
      methods.setError('payRate', { message: strings.memberPayCard.hourlyRateValidation });
      return;
    }

    if (!data.phoneNumber?.number && !data.email) return;

    if (currentLocationId && !data.locationIds.includes(currentLocationId.toString())) {
      methods.setError('locationIds', {
        message: strings.editTeamMember.unassignedFromClockedLocation,
        type: 'custom'
      });
      return;
    }

    const checkedLocationIds = mappedLocations.filter(l => l.checked).map(l => Number(l.key));

    const result = await updateEmployee({
      employee: toPayload(employee, data, checkedLocationIds)
    });

    await handleMutationResult(result, {
      successMessage: 'Team member successfully updated.',
      errorMessage: 'Unable to update team member.',
      onError: error => handleBEFormError(error, methods),
      onSuccess: goBack
    });
  };

  useEffect(() => {
    if (scrollPayIntoView) {
      paySectionRef.current?.scrollIntoView();
    }
  });

  return (
    <TsForm methods={methods} onSubmit={onSubmit}>
      <EmployeeAvatarEditCard
        avatarUrl={avatarUrl}
        employeeName={strings.employeeName(employee.firstName ?? '', employee.lastName ?? '')}
        fileInputRef={fileInputRef}
        loadingRemove={removingAvatar}
        loadingUpload={uploadingAvatar}
        onAvatarSelected={handleAvatarSelected}
        onRemoveAvatar={handleAvatarRemoved}
      />
      <TSTitleCard title={strings.cardTitle} className={TSTitleCardStyle}>
        <SubmissionErrorBoundary>
          <Suspense fallback={<TableLoader />}>
            <TeamMemberAddOrEditFormContent addMode={false} employee={employee} methods={methods} />
          </Suspense>
        </SubmissionErrorBoundary>
      </TSTitleCard>
      <Feature name="pay">
        <section ref={paySectionRef}>
          <TSTitleCard title={strings.memberPayCard.cardTitle} className={TSTitleCardStyle}>
            <SubmissionErrorBoundary>
              <Suspense fallback={<TableLoader />}>
                <div className="flex flex-row gap-2 w-[50%]">
                  <CurrencyField
                    id="payRate"
                    name="payRate"
                    title={strings.memberPayCard.hourlyRate}
                    validationMessage={strings.memberPayCard.hourlyRateValidation}
                    value={methods.watch('payRate')}
                    onChange={value => {
                      if (!value) throw new Error('No value');
                      methods.setValue('payRate', value);
                    }}
                  />
                </div>
              </Suspense>
            </SubmissionErrorBoundary>
          </TSTitleCard>
        </section>
      </Feature>
      <FormActionButtons loading={fetching} onCancel={goBack} />
    </TsForm>
  );
};
